import { unembed } from '@blackbird/common/embedding';
import { Link } from '@blackbird/ui-base';
import { useSessionStorage } from '@blackbird/ui-base/hooks';
import {
  AccessTime as AccessTimeIcon,
  Build as BuildIcon,
  Done as DoneIcon,
  OpenInBrowser,
  Report as ReportIcon,
} from '@mui/icons-material';
import { Button, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { amber, green, red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { useRouter } from 'next/router';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import AndonCallDialog from '@/components/dialogs/andon/call-interaction';
import Table, { Cell } from '@/components/tables/exportable-paginated';
import { SESSION_STORAGE_LINES_NAVIGATION_OVERVIEW_SEARCH_STATE_KEY } from '@/constants';
import { formatTimestamp } from '@/helpers/andon';
import { excelReadableDateFormat } from '@/helpers/helper-functions';
import { isWidthDown } from '@/hocs/with-width';
import { useWidth } from '@/hooks/use-width';

interface Properties {
  calls: Schema.AndonListCallsQuery['andon'][0]['calls'];
  // filters: Array<(call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]) => boolean>;
  filter?: (call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]) => boolean;
}

const useStyles = makeStyles()((theme) => ({
  disabledEm: {
    color: theme.palette.action.disabled,
  },
  '@keyframes pulseUrgencyCritical': {
    '0%': { backgroundColor: red['800'] },
    '50%': { backgroundColor: red['200'] },
    '100%': { backgroundColor: red['800'] },
  },
  '@keyframes pulseUrgencyMedium': {
    '0%': { backgroundColor: amber['600'] },
    '50%': { backgroundColor: amber['400'] },
    '100%': { backgroundColor: amber['600'] },
  },
  littlePadding: {
    [theme.breakpoints.down('md')]: {
      '& > *:first-child': {
        paddingLeft: theme.spacing(),
        paddingRight: theme.spacing(0.5),
      },
      '& > *, & > *:last-child': {
        paddingRight: theme.spacing(),
      },
    },
  },
  buttonIsPending: {
    backgroundColor: theme.palette.action.disabled,
  },
  buttonInProgress: {
    animationDuration: '3s !important',
  },
  buttonUrgencyCritical: {
    backgroundColor: red[700],
    animationName: '$pulseUrgencyCritical',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  backgroundUrgencyCritical: {
    backgroundColor: red[50],
  },
  buttonUrgencyMedium: {
    backgroundColor: amber[400],
    animationName: '$pulseUrgencyMedium',
    animationIterationCount: 'infinite',
    animationDuration: '750ms',
  },
  backgroundUrgencyMedium: {
    backgroundColor: amber[50],
  },
  backgroundPending: {
    backgroundImage:
      'repeating-linear-gradient(-45deg, #f9f7f7, #f9f7f7 10px, rgba(255, 255, 255, 0.075) 10px, rgba(255, 255, 255, 0.075) 20px)',
  },
  buttonResolved: {
    backgroundColor: green[400],
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    // tableLayout: 'fixed',
    overflow: 'auto',
  },
  truncate: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '25vw',
  },
  stretch: {
    maxWidth: '50vw',
  },
  openInBrowser: {
    paddingRight: theme.spacing(),
    verticalAlign: 'text-top',
  },
  exportButtonsAndPagination: {
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
    [theme.breakpoints.down('md')]: {
      display: 'grid-inline',
    },
  },
  hideOnMDUp: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  hideOnSMDown: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

const urgency = {
  [Schema.Urgency.CRITICAL]: 1,
  [Schema.Urgency.MEDIUM]: 0,
};

const sortCalls = <T extends Pick<Schema.Call, 'action' | 'urgency'>>(left: T, right: T) => {
  const leftMakeTime = new Date(left.action.make.time).getTime();
  const rightMakeTime = new Date(right.action.make.time).getTime();

  const leftTakeTime = new Date(left.action.take?.time!).getTime();
  const rightTakeTime = new Date(right.action.take?.time!).getTime();

  const leftResolveTime = new Date(left.action.resolve?.time!).getTime();
  const rightResolveTime = new Date(right.action.resolve?.time!).getTime();

  const leftPendingTime = new Date(left.action.pending?.time!).getTime();
  const rightPendingTime = new Date(right.action.pending?.time!).getTime();

  const isResolved = +!!leftResolveTime - +!!rightResolveTime;
  if (isResolved) {
    return isResolved;
  }

  const isPending = +!!leftPendingTime - +!!rightPendingTime;
  if (isPending) {
    return isPending;
  }

  const isTaken = +!!leftTakeTime - +!!rightTakeTime;
  if (isTaken) {
    return isTaken;
  }

  const urgencyDiff = urgency[right.urgency] - urgency[left.urgency];
  if (urgencyDiff) {
    return urgencyDiff;
  }

  const takeTime = rightTakeTime - leftTakeTime;
  if (takeTime) {
    return takeTime;
  }

  const makeTime = rightMakeTime - leftMakeTime;
  if (makeTime) {
    return makeTime;
  }

  return 0;
};

const AndonTable: FC<Properties> = ({ calls, filter }) => {
  const [t] = useTranslation();
  const { classes, cx } = useStyles();
  const width = useWidth();
  const isSmallScreen = isWidthDown('sm', width);

  const availableHeaders = useMemo(
    () => ({
      action: {
        key: 'action',
        label: () => t(['shared:action'], { defaultValue: 'Action' }),
        align: 'right',
        width: '80px',
      },
      line: {
        key: 'line',
        label: () => t(['shared:location'], { defaultValue: 'Location' }),
        className: classes.truncate,
      },
      machine: { key: 'machine', label: () => t(['shared:machine'], { defaultValue: 'Machine' }) },
      unit: { key: 'unit', label: () => t(['shared:unit'], { defaultValue: 'Unit' }), hideWhileSmall: true },
      callTime: { key: 'callTime', label: () => t(['andon:callTime'], { defaultValue: 'Call time' }) },
      takenTime: {
        key: 'takenTime',
        hideWhileSmall: true,
        label: () => t(['andon:takenTime'], { defaultValue: 'Taken time' }),
      },
      resolveTime: {
        key: 'resolveTime',
        hideWhileSmall: true,
        label: () => t(['andon:resolveTime'], { defaultValue: 'Resolve time' }),
      },
      caller: {
        key: 'caller',
        label: () => t(['andon:caller'], { defaultValue: 'Caller' }),
        hideWhileSmall: true,
      },
      taker: {
        key: 'taker',
        label: () => t(['andon:taker'], { defaultValue: 'Taker' }),
        hideWhileSmall: true,
      },
      issue: {
        key: 'issue',
        label: () => t(['shared:issue'], { defaultValue: 'Issue' }),
        className: classes.stretch,
        hideWhileSmall: true,
      },
      conclusion: {
        key: 'conclusion',
        label: () => t(['shared:conclusion'], { defaultValue: 'Conclusion' }),
        className: classes.stretch,
        hideWhileSmall: true,
      },
      workOrder: {
        key: 'workOrder',
        label: () => t(['apipro:workOrder'], { defaultValue: 'WorkOrder' }),
        className: classes.stretch,
        hideWhileSmall: true,
      },
    }),
    [classes, t],
  );

  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const router = useRouter();
  const [, setSearchState] = useSessionStorage(SESSION_STORAGE_LINES_NAVIGATION_OVERVIEW_SEARCH_STATE_KEY, {});
  const onNavigateLine = useCallback(
    () =>
      setSearchState({
        route: {
          pathname: router.pathname,
          query: router.query,
        },
      }),
    [setSearchState],
  );

  const clearCallSelection = useCallback(() => {
    setSelectedCallId(null);
  }, []);

  const selectedCall = calls.find((call) => call.id === selectedCallId);

  const filteredCalls = useMemo(() => (filter ? calls.filter(filter) : calls).sort(sortCalls), [filter, calls]);

  const anyTagged = useMemo(() => filteredCalls.some(({ tags }) => !!tags?.length), [filteredCalls]);

  const rows = useMemo(
    () =>
      filteredCalls.map((call) => {
        const pending = !!call?.action?.pending?.time;
        const resolved = !!call?.action?.resolve?.time;

        // An item cannot be in progress while resolved - this is just to avoid (and ensure) invalid inProgress value.
        const inProgress = !resolved && !!call?.action?.take?.time;
        const isPending = pending && !resolved;

        const [IconComponent, actionMessage] = (() => {
          switch (true) {
            case resolved:
              return [
                DoneIcon,
                t(['andon:call:resolved'], { defaultValue: 'This call has been resolved, click to see details' }),
              ];
            case pending:
              return [
                AccessTimeIcon,
                t(['andon:workOrder:pending'], {
                  defaultValue: "A work order has been initiated for this call and it's in a pending state",
                }),
              ];
            case inProgress:
              return [BuildIcon, t(['andon:call:resolve'], { defaultValue: 'Resolve call' })];
            default:
              return [ReportIcon, t(['andon:call:take'], { defaultValue: 'Take call' })];
          }
        })();

        const isCritical = call.urgency === Schema.Urgency.CRITICAL;

        const buttonClass = cx({
          [classes.buttonResolved]: resolved,
          [classes.buttonUrgencyCritical]: isCritical && !resolved && !isPending,
          [classes.buttonUrgencyMedium]: !isCritical && !resolved && !isPending,
          [classes.buttonInProgress]: inProgress,
          [classes.buttonIsPending]: isPending,
        });

        const backgroundClass = cx({
          [classes.backgroundUrgencyCritical]: isCritical && !resolved && !inProgress,
          [classes.backgroundUrgencyMedium]: !isCritical && !resolved && !inProgress,
          [classes.backgroundPending]: isPending,
        });

        const cells: Array<Cell> = [];

        cells.push(
          {
            key: 'action',
            // className: classes.noWrap,
            children: (
              <Tooltip placement={'left'} title={actionMessage}>
                <Button
                  className={buttonClass}
                  variant="contained"
                  color="primary"
                  onClick={() => setSelectedCallId(call.id)}
                >
                  <IconComponent />
                </Button>
              </Tooltip>
            ),
          },
          {
            key: 'line',
            children: (
              <Tooltip title={call.line ? t(['shared:openLine'], { defaultValue: 'Open line' }) : ''}>
                <Typography onClick={onNavigateLine} className={classes.truncate}>
                  {call.line ? (
                    <Link href={{ pathname: '/line', query: { lineId: call.line.id, tab: 'register-stops' } }}>
                      {isSmallScreen ? null : (
                        <span className={classes.openInBrowser}>
                          <OpenInBrowser fontSize="small" />
                        </span>
                      )}
                      {call.line.name}
                    </Link>
                  ) : (
                    <em className={classes.disabledEm}>{t(['shared:deleted'], { defaultValue: 'Deleted' })}</em>
                  )}
                </Typography>
              </Tooltip>
            ),
          },
        );

        const [tag] = call.tags || [];

        if (anyTagged) {
          const na = t(['shared:notAvailable'], { defaultValue: 'N/A' });
          const [machine = na, unit = na] = tag?.value.split(':') || [];

          cells.push(
            {
              key: ['machine', call.id].join('-'),
              children: machine,
            },
            {
              key: ['unit', call.id].join('-'),
              children: unit,
            },
          );
        }

        cells.push({
          key: ['makeTime', call.id].join('-'),
          children: formatTimestamp(call?.action?.make?.time),
        });

        if (!isSmallScreen) {
          const [unembeddedPendingDetails] = unembed(call?.action?.pending?.details ?? '');
          cells.push(
            {
              key: ['takeTime', call.id].join('-'),
              children: formatTimestamp(call?.action?.take?.time),
            },
            {
              key: ['resolveTime', call.id].join('-'),
              children: formatTimestamp(call?.action?.resolve?.time),
            },
            {
              key: ['caller', call.id].join('-'),
              children: (
                <Typography className={cx(classes.truncate, classes.noWrap)}>
                  {call?.action?.make?.author ?? ''}
                </Typography>
              ),
            },
            {
              key: ['taker', call.id].join('-'),
              children: (
                <Typography className={cx(classes.truncate, classes.noWrap)}>
                  {call?.action?.take?.author ?? ''}
                </Typography>
              ),
            },
            {
              key: ['issue', call.id].join('-'),
              children: <Typography>{call?.action?.make?.details ?? null}</Typography>,
            },
            {
              key: ['conclusion', call.id].join('-'),
              children: (
                <Typography>
                  {unembeddedPendingDetails ? (
                    <>
                      {unembeddedPendingDetails}
                      <br />
                    </>
                  ) : null}

                  {call?.action?.resolve?.details || null}
                </Typography>
              ),
            },
          );
        }

        return {
          key: call.id,
          className: backgroundClass,
          cells,
        };
      }),
    [filteredCalls, isSmallScreen],
  );

  const headers = useMemo(() => {
    return [
      availableHeaders.action,
      availableHeaders.line,
      anyTagged ? availableHeaders.machine : [],
      anyTagged ? availableHeaders.unit : [],
      availableHeaders.callTime,
      isSmallScreen ? [] : availableHeaders.takenTime,
      isSmallScreen ? [] : availableHeaders.resolveTime,
      isSmallScreen ? [] : availableHeaders.caller,
      isSmallScreen ? [] : availableHeaders.taker,
      isSmallScreen ? [] : availableHeaders.issue,
      isSmallScreen ? [] : availableHeaders.conclusion,
    ].flatMap((header) => {
      if (Array.isArray(header)) {
        return header;
      }

      const { label, ...attributes } = header;
      return {
        ...attributes,
        label: label(),
      };
    });
  }, [anyTagged, isSmallScreen]);

  const onExport = useCallback(() => {
    return {
      headers: [
        { label: t('shared:identifier', { defaultValue: 'Identifier' }), key: 'id' },
        { label: t('andon:callTimestamp', { defaultValue: 'Call Timestamp' }), key: 'callTime' },
        { label: t('andon:takenTimestamp', { defaultValue: 'Taken Timestamp' }), key: 'takeTime' },
        { label: t('andon:waitDurationMinutes', { defaultValue: 'Wait Duration (min)' }), key: 'waitDuration' },
        anyTagged
          ? { label: t('andon:pendingTimestamp', { defaultValue: 'Pending Timestamp' }), key: 'pendingTime' }
          : null,
        anyTagged
          ? {
              label: t('andon:wordOrderDurationMinutes', { defaultValue: 'Work Order Duration (min)' }),
              key: 'workOrderDuration',
            }
          : null,
        { label: t('andon:resolveTimestamp', { defaultValue: 'Resolution Timestamp' }), key: 'resolveTime' },
        {
          label: t('andon:resolveDurationMinutes', { defaultValue: 'Resolve Duration (min)' }),
          key: 'resolveDuration',
        },
        { label: t('andon:requestedRole', { defaultValue: 'Requested Role' }), key: 'requestedSupport' },
        { label: t('andon:callInitiator', { defaultValue: 'Caller' }), key: 'caller' },
        { label: t('andon:callTaker', { defaultValue: 'Taker' }), key: 'taker' },
        { label: t('shared:issue', { defaultValue: 'Issue' }), key: 'issue' },
        { label: t('shared:conclusion', { defaultValue: 'Conclusion' }), key: 'conclusion' },
        { label: t('andon:urgency', { defaultValue: 'Urgency' }), key: 'urgency' },
        { label: t(['shared:location'], { defaultValue: 'Location' }), key: 'location' },
        anyTagged ? { label: t('andon:workOrder:name', { defaultValue: 'Work Order' }), key: 'workOrder' } : null,
      ].filter((header): header is NonNullable<typeof header> => !!header),
      data: filteredCalls.map((call) => {
        const makeTime = new Date(call.action.make.time).getTime();
        const takeTime = call.action.take && new Date(call.action.take?.time).getTime();
        const pendingTime = call.action.pending && new Date(call.action.pending.time).getTime();
        const resolveTime = call.action.resolve && new Date(call.action.resolve.time).getTime();
        const waitDuration = typeof takeTime === 'number' ? Math.floor((takeTime - makeTime) / (1).minutes) : '';
        const resolveDuration =
          typeof resolveTime === 'number' ? Math.floor((resolveTime - (pendingTime || takeTime!)) / (1).minutes) : '';

        const timeOfCall = excelReadableDateFormat(call.action.make.time);
        const timeTaken = call.action.take?.time ? excelReadableDateFormat(call.action.take?.time) : '';
        const timeResolved = call.action.resolve?.time ? excelReadableDateFormat(call.action.resolve?.time) : '';

        const issue = call.action.make.details;
        const [unembeddedDetails, codes] = unembed(call.action.pending?.details || '');
        const conclusion = [
          unembeddedDetails,
          Object.entries(codes || {})
            .map(([key, value]) => `${value} (${key})`)
            .join(', '),
          call.action.resolve?.details,
        ]
          .join('\n')
          .trim();

        const requestedSupport =
          call.role?.name || call.requestedSupport || t(['shared:anyone'], { defaultValue: 'Anyone' }) || '';
        const caller = call.action.make.author;
        const taker = call.action.take?.author || '';

        const data: Record<string, string | number | Date> = {
          id: call.id,
          callTime: timeOfCall,
          takeTime: timeTaken,
          waitDuration,
          resolveTime: timeResolved,
          resolveDuration,
          requestedSupport,
          caller,
          taker,
          issue,
          conclusion,
          urgency: call.urgency,
          location: call.line?.name ?? '',
        };

        if (anyTagged) {
          const workOrderDuration =
            typeof pendingTime === 'number' ? Math.floor((pendingTime - makeTime) / (1).minutes) : '';
          const timePending = call.action.pending?.time ? excelReadableDateFormat(call.action.pending?.time) : '';

          const [, workOrder] = unembeddedDetails.split(/Work Order: #/);

          data.workOrderDuration = workOrderDuration;
          data.pendingTime = timePending;
          data.workOrder = workOrder;
        }

        return data;
      }),
    };
  }, [filteredCalls]);

  return (
    <>
      <Table rows={rows} headers={headers} onExport={onExport}>
        <TableRow key="empty" className={cx(classes.littlePadding)}>
          <TableCell align="center" colSpan={headers.length}>
            <Typography variant="body2" align="center">
              {calls.length
                ? t(['andon:emptyCallTableFiltered'], { defaultValue: 'No Andon calls matches the active filters.' })
                : t(['andon:emptyCallTable'], { defaultValue: 'No Andon calls have been made.' })}
            </Typography>
          </TableCell>
        </TableRow>
      </Table>
      {selectedCall ? (
        <AndonCallDialog call={selectedCall} open={!!selectedCall} handleOpenClose={clearCallSelection} />
      ) : null}
    </>
  );
};

export default AndonTable;
